// Photoshoot calendar, booking, and proofs.
// Year-scoped docs live on iabf-config-{year}/photoshoot and
// iabf-photoshoot-slots-{year}.

import { PHOTOSHOOT_EXTRA_GIFT_LABELS, PHOTOSHOOT_GIFT_LABELS, selectedGiftsFromDancer } from './iabf-gifts'
import { festivalWeek } from './iabf-festival-dates'

export const PHOTOSHOOT_HOLD_MS = 5 * 60 * 1000
export const PHOTOSHOOT_SLOT_MINUTES = 15
export const PHOTOSHOOT_PRICE_CENTS = 8500
export const PHOTOSHOOT_EDITED_PER_SLOT = 2
export const PHOTOSHOOT_EXTRA_PHOTO_CENTS = 3000
export const PHOTOSHOOT_ALL_PROOFS_CENTS = 20000
export const PHOTOSHOOT_JULIE_CHOOSE_CENTS = 3000

export const PHOTOSHOOT_SETTINGS = [
  { value: 'indoor', label: 'Indoor' },
  { value: 'outdoor', label: 'Outdoor' }
]

export const DEFAULT_PHOTOSHOOT_CONFIG = {
  indoorLocation: 'Sculpture room',
  outdoorLocation: 'Urban amphitheater',
  defaultDirectorName: 'Julie Gill',
  defaultDirectorId: '',
  defaultEditorName: '',
  defaultEditorId: '',
  defaultEditorUid: '',
  defaultEditorEmail: '',
  slotPriceCents: PHOTOSHOOT_PRICE_CENTS,
  editedPhotosPerSlot: PHOTOSHOOT_EDITED_PER_SLOT,
  extraPhotoCents: PHOTOSHOOT_EXTRA_PHOTO_CENTS,
  allProofsCents: PHOTOSHOOT_ALL_PROOFS_CENTS,
  julieChooseCents: PHOTOSHOOT_JULIE_CHOOSE_CENTS,
  cancelUntilIso: '',
  // Dancers cannot book until this is on. Julie can preview the schedule anyway.
  dancerBookingOpen: false
}

/** Only this login sees dancer booking while dancerBookingOpen is off. */
export const PHOTOSHOOT_PREVIEW_EMAIL = 'julie@brocheballet.com'

export function canSeeDancerPhotoshootSchedule (email, dancerBookingOpen) {
  if (dancerBookingOpen) {
    return true
  }
  return String(email || '').trim().toLowerCase() === PHOTOSHOOT_PREVIEW_EMAIL
}

/** Corps participation fields used to block photoshoots during group rehearsals. */
export const PHOTOSHOOT_CORPS_FIELDS = [
  { field: 'aliceInWonderland', name: 'Alice in Wonderland' },
  { field: 'becoming', name: 'Becoming' },
  { field: 'waltzOfHours', name: 'Waltz of the Hours' },
  { field: 'leJazzHot', name: 'Le Jazz Hot' },
  { field: 'garlandWaltz', name: 'Garland Waltz' }
]

const WEEKDAY_OFFSET = {
  monday: 0,
  tuesday: 1,
  wednesday: 2,
  thursday: 3,
  friday: 4,
  saturday: 5,
  sunday: 6
}

export function parseTimeToMinutes (hhmm) {
  const s = String(hhmm || '').trim()
  const m = s.match(/^(\d{1,2}):(\d{2})$/)
  if (!m) {
    return null
  }
  const h = Number(m[1])
  const min = Number(m[2])
  if (!Number.isFinite(h) || !Number.isFinite(min) || h < 0 || h > 23 || min < 0 || min > 59) {
    return null
  }
  return h * 60 + min
}

export function minutesToTime (total) {
  const n = Math.round(Number(total) || 0)
  const h = Math.floor(n / 60)
  const m = n % 60
  return String(h).padStart(2, '0') + ':' + String(m).padStart(2, '0')
}

export function formatSlotClock (totalMinutes) {
  const n = Math.round(Number(totalMinutes) || 0)
  let h = Math.floor(n / 60)
  const m = n % 60
  const ampm = h >= 12 ? 'PM' : 'AM'
  h = h % 12
  if (h === 0) {
    h = 12
  }
  return h + ':' + String(m).padStart(2, '0') + ' ' + ampm
}

export function formatSlotRange (startMin, endMin) {
  return formatSlotClock(startMin) + ' – ' + formatSlotClock(endMin)
}

export function locationForSetting (config, setting) {
  const c = config || {}
  if (setting === 'outdoor') {
    return String(c.outdoorLocation || DEFAULT_PHOTOSHOOT_CONFIG.outdoorLocation)
  }
  return String(c.indoorLocation || DEFAULT_PHOTOSHOOT_CONFIG.indoorLocation)
}

/**
 * Split a block of time into 15-minute slots. The last partial window is dropped.
 * @returns {Array<{ startMin: number, endMin: number }>}
 */
export function splitBlockIntoSlots (startHhmm, endHhmm, minutes = PHOTOSHOOT_SLOT_MINUTES) {
  const startMin = parseTimeToMinutes(startHhmm)
  const endMin = parseTimeToMinutes(endHhmm)
  const step = Number(minutes) || PHOTOSHOOT_SLOT_MINUTES
  if (startMin == null || endMin == null || endMin <= startMin || step < 1) {
    return []
  }
  const out = []
  for (let t = startMin; t + step <= endMin; t += step) {
    out.push({ startMin: t, endMin: t + step })
  }
  return out
}

export function photoshootSlotId (date, setting, startMin) {
  return [date, setting, startMin].join('_')
}

export function giftPhotoshootSlotsFromDancer (dancer) {
  const selected = selectedGiftsFromDancer(dancer)
  let n = 0
  selected.forEach((label) => {
    if (PHOTOSHOOT_GIFT_LABELS.includes(label)) {
      n += 1
    }
  })
  return n
}

/** Extra-edited-photo gift still counts for 2026 dancers; not offered in 2027. */
export function extraGiftEditedCountFromDancer (dancer) {
  const selected = selectedGiftsFromDancer(dancer)
  let n = 0
  selected.forEach((label) => {
    if (PHOTOSHOOT_EXTRA_GIFT_LABELS.includes(label)) {
      n += 3
    }
  })
  return n
}

export function photoshootChargeCents (slotCount, giftSlots, priceCents = PHOTOSHOOT_PRICE_CENTS) {
  const slots = Math.max(0, Math.round(Number(slotCount) || 0))
  const gifts = Math.max(0, Math.round(Number(giftSlots) || 0))
  const paid = Math.max(0, slots - gifts)
  const price = Math.max(0, Math.round(Number(priceCents) || PHOTOSHOOT_PRICE_CENTS))
  return paid * price
}

export function includedEditedCount (slotCount, extraGiftPhotos = 0) {
  const slots = Math.max(0, Math.round(Number(slotCount) || 0))
  const extras = Math.max(0, Math.round(Number(extraGiftPhotos) || 0))
  return slots * PHOTOSHOOT_EDITED_PER_SLOT + extras
}

export function extraEditedChargeCents (selectedCount, included, extraPhotoCents = PHOTOSHOOT_EXTRA_PHOTO_CENTS) {
  const selected = Math.max(0, Math.round(Number(selectedCount) || 0))
  const incl = Math.max(0, Math.round(Number(included) || 0))
  const price = Math.max(0, Math.round(Number(extraPhotoCents) || PHOTOSHOOT_EXTRA_PHOTO_CENTS))
  return Math.max(0, selected - incl) * price
}

export function isoDateForFestivalWeekday (year, dayKey) {
  const week = festivalWeek(year)
  const offset = WEEKDAY_OFFSET[String(dayKey || '').toLowerCase()]
  if (offset == null || !week || !week.arrivalIso) {
    return ''
  }
  const [y, m, d] = String(week.arrivalIso).split('-').map(Number)
  if (!y || !m || !d) {
    return ''
  }
  const dt = new Date(y, m - 1, d + offset)
  const yy = dt.getFullYear()
  const mm = String(dt.getMonth() + 1).padStart(2, '0')
  const dd = String(dt.getDate()).padStart(2, '0')
  return yy + '-' + mm + '-' + dd
}

function normTitle (s) {
  return String(s || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()
}

export function groupPieceNamesForDancer (dancer, pieces, groupPieces) {
  const names = []
  const seen = {}
  const add = (name) => {
    const n = String(name || '').trim()
    const key = normTitle(n)
    if (!n || seen[key]) {
      return
    }
    seen[key] = true
    names.push(n)
  }
  PHOTOSHOOT_CORPS_FIELDS.forEach((row) => {
    const rec = dancer && dancer[row.field]
    if (rec && rec.participating) {
      add(row.name)
    }
  })
  ;(pieces || []).forEach((p) => {
    const raw = Array.isArray(p && p.dancers) ? p.dancers : []
    const size = Number(p && p.groupSize) || raw.length || 1
    const type = String((p && p.pieceType) || '').toLowerCase()
    const isGroup = size > 1 || type.indexOf('group') !== -1
    if (isGroup) {
      add(p.title || p.programTitle)
    }
  })
  const dancerId = dancer && dancer.id
  ;(groupPieces || []).forEach((p) => {
    const ids = (p && p.allDancerIds) || []
    if (dancerId && ids.indexOf(dancerId) !== -1) {
      add(p.title)
    }
  })
  return names
}

export function groupPieceIdsForDancer (dancer, groupPieces) {
  const dancerId = dancer && dancer.id
  if (!dancerId) {
    return []
  }
  return (groupPieces || []).filter((p) => {
    const ids = (p && p.allDancerIds) || []
    return ids.indexOf(dancerId) !== -1
  }).map((p) => p.id)
}

/**
 * Festival-week rehearsal windows for group pieces this dancer is in.
 * Prefers itinerary classes tagged with groupPieceId (2027+).
 * Title matching remains as a fallback for 2026 corps / showcase groups.
 * @returns {Array<{ date: string, startMin: number, endMin: number, title: string }>}
 */
export function rehearsalWindowsForDancer ({ year, classes, dancer, pieces, groupPieces }) {
  const names = groupPieceNamesForDancer(dancer, pieces, groupPieces).map(normTitle).filter(Boolean)
  const groupIds = groupPieceIdsForDancer(dancer, groupPieces)
  const windows = []
  ;(classes || []).forEach((cls) => {
    const date = isoDateForFestivalWeekday(year, cls.day)
    const startMin = parseTimeToMinutes(cls.startTime)
    const endMin = parseTimeToMinutes(cls.endTime)
    if (!date || startMin == null || endMin == null || endMin <= startMin) {
      return
    }
    const byId = cls.groupPieceId && groupIds.indexOf(cls.groupPieceId) !== -1
    if (!byId) {
      const tracks = Array.isArray(cls && cls.tracks)
        ? cls.tracks
        : (cls && cls.track ? [cls.track] : [])
      if (tracks.indexOf('rehearsal') === -1) {
        return
      }
      const title = normTitle(cls && cls.title)
      if (!title || !names.length) {
        return
      }
      const matched = names.some((n) => title.indexOf(n) !== -1 || n.indexOf(title) !== -1)
      if (!matched) {
        return
      }
    }
    windows.push({
      date,
      startMin,
      endMin,
      title: String((cls && cls.title) || 'rehearsal')
    })
  })
  return windows
}

export function photoshootConflictLabel (slot, windows) {
  const date = String((slot && slot.date) || '')
  const startMin = Number(slot && slot.startMin)
  const endMin = Number(slot && slot.endMin)
  if (!date || !Number.isFinite(startMin) || !Number.isFinite(endMin)) {
    return ''
  }
  for (let i = 0; i < (windows || []).length; i++) {
    const w = windows[i]
    if (w.date === date && rangesOverlap(startMin, endMin, w.startMin, w.endMin)) {
      return 'Same time as ' + w.title + ' rehearsal'
    }
  }
  return ''
}

export function slotIsInTheFuture (slot, now = new Date()) {
  const date = String((slot && slot.date) || '')
  const startMin = Number(slot && slot.startMin)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !Number.isFinite(startMin)) {
    return false
  }
  // Festival times are Eastern. Compare as America/New_York wall time.
  const iso = date + 'T' + minutesToTime(startMin) + ':00'
  try {
    const asEt = new Date(iso + '-04:00')
    if (Number.isNaN(asEt.getTime())) {
      return false
    }
    return asEt.getTime() > now.getTime()
  } catch (e) {
    return false
  }
}

export function rangesOverlap (aStart, aEnd, bStart, bEnd) {
  return aStart < bEnd && bStart < aEnd
}
